import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { maybeConfig } from "../config";
import { Button } from "../ui/Button";
import { currentServer, serversQuery } from "./servers";

/**
 * Profile › Servers (WEB-SRV-LST-02, -03, SRV-27): every server of servers.json with its label and kind, this one
 * marked "You are here", each other one a link to its `web` origin in the same tab. The address is the server, so
 * nothing is chosen or signed out here.
 */
export function ServersDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const list = useQuery({ ...serversQuery(), enabled: open });
  const environment = maybeConfig()?.environment ?? "production";
  const here = list.data ? currentServer(list.data, environment, window.location.origin) : undefined;

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={titleId}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      {open && (
        <div className="p-6">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            {t("servers.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-800">{t("servers.intro")}</p>
          {list.isPending ? (
            <p className="mt-4 text-sm text-gray-700">{t("app.loading")}</p>
          ) : list.isError ? (
            <>
              <ul className="mt-4 divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
                <li className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="font-medium text-gray-900">{t(`servers.kind.${environment}`)}</span>
                  <span className="text-xs font-medium text-gray-800">{t("servers.here")}</span>
                </li>
              </ul>
              <p role="alert" className="mt-3 text-sm text-red-800">
                {t("servers.unreadable")}
              </p>
            </>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
              {list.data.map((s) => (
                <li
                  key={s.id}
                  aria-current={s === here ? "true" : undefined}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span className="min-w-0">
                    {s === here ? (
                      <span className="block font-medium text-gray-900">{s.label}</span>
                    ) : (
                      <a href={s.web} className="block font-medium text-brand underline-offset-2 hover:underline">
                        {s.label}
                      </a>
                    )}
                    <span className="block text-xs text-gray-700">{t(`servers.line.${s.kind}`)}</span>
                  </span>
                  {s === here && (
                    <span className="shrink-0 text-xs font-medium text-gray-800">{t("servers.here")}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-6 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              {t("servers.close")}
            </Button>
          </div>
        </div>
      )}
    </dialog>
  );
}
